import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import PSUpsellBanner from '@/components/PSUpsellBanner';

/**
 * ── /components/ps-upsell-banner ─────────────────────────────────────────
 *
 * PSUpsellBanner — the Access Pass showreel hero from home's non-member
 * entry point (`app/index.tsx`'s `listHeader`, rendered when
 * `!holdsAMembership`; that call site also wraps it in a `$color.brandDark`,
 * negative-margin `View` that bleeds it full-width past the page gutter —
 * page-level chrome, not something this component owns or this preview
 * reproduces). A LIVE, autoplaying, muted, looping video with a pitch + CTA
 * overlay — this is the one gallery preview you have to eyeball running,
 * not just read: tap the frame to toggle sound and watch the speaker glyph
 * follow the player's real `mutedChange` event, tap the CTA to confirm it
 * fires `onPressCta` (a counter below each instance) instead of also
 * toggling mute — RN gives the press to the innermost responder and does
 * NOT bubble it, so the two taps can't cross-trigger.
 *
 * The component's own prop surface is exactly `{ ctaTitle, onPressCta? }` —
 * no `pitch`/title prop exists. Its docstring talks about "the pitch and
 * one CTA sitting on top of it", but nothing in its render tree draws pitch
 * text; the reel is graded/cut with the pitch baked into the footage
 * itself, and there's deliberately no scrim (see the component's
 * docstring — a bet on this footage staying dark enough behind the text
 * that IS rendered, i.e. the CTA). The reel URL (`SHOWREEL_URL`) is
 * hardwired too, not a prop — there is exactly one Access Pass showreel.
 *
 * DISCREPANCY vs the old `all.tsx` coverage (lines 281-308, now fully
 * superseded by this route): its comment claimed "Two title lengths,
 * since the copy comes from the API and a long pitch has to wrap" — but
 * since no pitch prop exists, that was never true. The two old blocks only
 * ever varied `ctaTitle`, and that's still all there is to vary here; the
 * comment overstated what the two blocks demonstrated. This file names the
 * axis correctly: CTA label length only.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSUpsellBannerPreview() {
  // Tap counters — separate per instance so each video's CTA is provably
  // independent of the other, and both are provably independent of that
  // same instance's mute toggle (tapping the frame never moves this number).
  const [shortCtaTaps, setShortCtaTaps] = useState(0);
  const [longCtaTaps, setLongCtaTaps] = useState(0);

  return (
    <>
      <Head>
        <title>PSUpsellBanner · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* The real call site's exact copy — home's non-member Access Pass
            entry, which routes to /memberships. Tap the video to mute/unmute
            (speaker glyph tracks the player, not local state — see the
            component's docstring on why); tap the CTA to fire onPressCta and
            watch the counter move, confirming it's a distinct gesture from
            mute. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — home&apos;s real copy (&quot;Check out Access Pass&quot;);
              tap the video to mute/unmute, tap the CTA to fire onPressCta (
              {shortCtaTaps} fired)
            </Text>
            <PSUpsellBanner
              ctaTitle="Check out Access Pass"
              onPressCta={() => setShortCtaTaps((n) => n + 1)}
            />
          </YStack>

          {/* Long ctaTitle — PSButton's label wraps to two lines inside the
            fixed-height overlay instead of pushing past the frame or
            shoving the speaker glyph out of its corner. Copy here is
            deliberately longer than anything the API currently sends, to
            force the wrap on purpose rather than hope a real string someday
            does it. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              long ctaTitle — wraps to two lines, button stays pinned to the
              bottom of the frame ({longCtaTaps} fired)
            </Text>
            <PSUpsellBanner
              ctaTitle="Check out the full Access Pass membership plans now"
              onPressCta={() => setLongCtaTaps((n) => n + 1)}
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
